import React, { useEffect, useRef } from 'react';

interface FloatingTextProps {
  onActivity: () => void;
}

interface TextElement {
  x: number;
  y: number;
  dx: number;
  dy: number;
  text: string;
  opacity: number;
  size: number;
}

export const FloatingText: React.FC<FloatingTextProps> = ({ onActivity }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>(0);
  const elementsRef = useRef<TextElement[]>([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const texts = ['MapleOS', 'Professional', 'Innovative', 'Secure', 'Modern'];

    const initElements = () => {
      elementsRef.current = [];
      for (let i = 0; i < 8; i++) {
        elementsRef.current.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          dx: (Math.random() - 0.5) * 2,
          dy: (Math.random() - 0.5) * 2,
          text: texts[Math.floor(Math.random() * texts.length)],
          opacity: Math.random() * 0.4 + 0.1,
          size: Math.random() * 30 + 20
        });
      }
    };

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initElements();
    };
    
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    const animate = () => {
      // Clear canvas with gradient background
      const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
      gradient.addColorStop(0, '#1a1a2e');
      gradient.addColorStop(0.5, '#16213e');
      gradient.addColorStop(1, '#0f1419');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      elementsRef.current.forEach(element => {
        // Update position
        element.x += element.dx;
        element.y += element.dy;

        // Bounce off walls
        if (element.x <= 0 || element.x >= canvas.width) {
          element.dx = -element.dx;
        }
        if (element.y <= 0 || element.y >= canvas.height) {
          element.dy = -element.dy;
        }

        // Keep within bounds
        element.x = Math.max(0, Math.min(canvas.width, element.x));
        element.y = Math.max(0, Math.min(canvas.height, element.y));

        // Draw text with glow effect
        ctx.font = `${element.size}px 'Arial', sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        
        // Glow effect
        ctx.shadowColor = '#64b5f6';
        ctx.shadowBlur = 15;
        ctx.fillStyle = `rgba(100, 181, 246, ${element.opacity})`;
        ctx.fillText(element.text, element.x, element.y);
        
        // Main text
        ctx.shadowBlur = 0;
        ctx.fillStyle = `rgba(255, 255, 255, ${element.opacity * 1.5})`;
        ctx.fillText(element.text, element.x, element.y);
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <div 
      className="fixed inset-0 cursor-none z-[9999]"
      onClick={onActivity}
      onMouseMove={onActivity}
      onKeyDown={onActivity}
    >
      <canvas 
        ref={canvasRef}
        className="w-full h-full"
      />
      <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 text-white text-lg opacity-60">
        Corporate elegance in motion... Move mouse or press any key to exit
      </div>
    </div>
  );
};
